import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Developer flags — opt-in UI surfaces intended for app developers
 * diagnosing issues against the real API, not for end users. Each flag
 * defaults to OFF; users flip them from the "Developer" card in
 * Settings (visible only under real-auth sessions).
 *
 * Persisted to localStorage as "whb-dev-flags" so the preference
 * survives refreshes. Add new flags by extending the interface + the
 * default object; don't rename existing keys or old values will be
 * silently dropped on load.
 *
 * Scope: these flags should be cheap to leave OFF. If a flag becomes
 * load-bearing for real users it probably graduated from dev-only and
 * needs a different home.
 */
export interface DevFlags {
  /**
   * Show the `/api/broker-accounts` probe panel on the Accounts page.
   * Lets us hit arbitrary `/api/*` paths and read the raw JSON while
   * the real-data wiring is incomplete.
   */
  debugPanelEnabled: boolean;
}

interface DevFlagsState extends DevFlags {
  setFlag: <K extends keyof DevFlags>(key: K, value: DevFlags[K]) => void;
  toggleFlag: (key: keyof DevFlags) => void;
}

const DEFAULTS: DevFlags = {
  debugPanelEnabled: false,
};

export const useDevFlagsStore = create<DevFlagsState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setFlag: (key, value) => set({ [key]: value } as Pick<DevFlags, typeof key>),
      toggleFlag: (key) =>
        set((s) => ({ [key]: !s[key] } as Pick<DevFlags, typeof key>)),
    }),
    {
      name: "whb-dev-flags",
      storage: createJSONStorage(() => localStorage),
      // Merge defaults on rehydrate so new flags added later start OFF
      // for users who already have a persisted object without them.
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<DevFlags>),
      }),
    }
  )
);
